import usePageTitle from '../hooks/usePageTitle';
import React, { FormEvent, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Droplets } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  usePageTitle('Login');
  const { session, signInWithOtp } = useAuth();
  const location = useLocation();
  const redirectTo =
    (location.state as { from?: string } | null)?.from ?? '/';
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset the success state if the user navigates back to /login.
  useEffect(() => {
    setSent(false);
    setError(null);
  }, [location.pathname]);

  if (session) {
    return <Navigate to={redirectTo} replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError(null);
    const { error: err } = await signInWithOtp(email.trim().toLowerCase());
    setSubmitting(false);
    if (err) {
      setError(err);
      return;
    }
    setSent(true);
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--background, #f6f8fb)',
        padding: '2rem',
      }}
    >
      <div
        className="card"
        style={{
          maxWidth: 420,
          width: '100%',
          padding: '2rem',
          boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
          <Droplets size={28} color="var(--primary-blue, #0077be)" />
          <h1 style={{ margin: 0, fontSize: '1.4rem' }}>H2Okay</h1>
        </div>

        {sent ? (
          <>
            <h2 style={{ marginTop: 0 }}>Check your inbox</h2>
            <p style={{ color: 'var(--text-secondary)' }}>
              We sent a sign-in link to <strong>{email}</strong>. Click the link to
              continue. The link expires after one hour.
            </p>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setSent(false)}
            >
              Use a different email
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <h2 style={{ marginTop: 0 }}>Sign in</h2>
            <p style={{ color: 'var(--text-secondary)', marginTop: 0 }}>
              Enter your email and we'll send you a magic link.
            </p>
            <label htmlFor="login-email" style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
              Email
            </label>
            <input
              id="login-email"
              className="form-input"
              type="email"
              required
              autoFocus
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: '100%', marginBottom: '1rem' }}
            />
            {error && (
              <div style={{ color: 'var(--danger, #d33)', marginBottom: '0.75rem', fontSize: '0.875rem' }}>
                {error}
              </div>
            )}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || !email.trim()}
              style={{ width: '100%' }}
            >
              {submitting ? 'Sending…' : 'Send magic link'}
            </button>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem', marginTop: '1.25rem' }}>
              Don't have an account? Access is invite-only — ask your consultant.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;
